import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, RefreshCw, CheckCircle2, XCircle, KeyRound, Pencil, Cloud } from "lucide-react";
import { CloudIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";
import { statusLabel, submitLabel } from "@/components/git-connectors";

export const PLATFORMS = [
  { key: "azure", label: "Azure", cli: "az" },
  { key: "aws", label: "AWS", cli: "aws" },
  { key: "gcp", label: "Google Cloud", cli: "gcloud" },
];

const submitLabelFor = (saving, isEdit) => (saving ? "Testing credentials…" : submitLabel(false, isEdit));

function PlatformPicker({ value, onChange, disabled, hint }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">Cloud provider</Label>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {PLATFORMS.map((p) => (
          <button key={p.key} type="button" disabled={disabled} onClick={() => onChange(p.key)} data-testid={`platform-pick-${p.key}`}
            className={cn("flex items-center gap-2 border px-3 py-2.5 text-left text-sm transition-colors", value === p.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400", disabled && value !== p.key && "opacity-40")}>
            <CloudIcon cloud={p.key} className="h-4 w-4" />
            <span className="font-display font-semibold">{p.label}</span>
          </button>
        ))}
      </div>
      <p className="mt-1.5 font-mono-tight text-[11px] text-slate-500">{hint}</p>
    </div>
  );
}

function CredentialField({ field, value, onChange, isEdit }) {
  const testid = `platform-field-${field.key}`;
  const keepHint = isEdit && field.secret && <span className="normal-case tracking-normal text-slate-400">(stored — leave empty to keep)</span>;
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{field.label} {keepHint}</Label>
      {field.multiline ? (
        <Textarea required={!isEdit} data-testid={testid} className="mt-2 min-h-[120px] border-slate-200 font-mono-tight text-xs" placeholder={isEdit ? "Paste a new key to rotate it" : '{ "type": "service_account", ... }'} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input required={!isEdit || !field.secret} type={field.secret ? "password" : "text"} autoComplete="off" data-testid={testid} className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export function PlatformForm({ open, onOpenChange, onSaved, providers, editing = null, initialPlatform = "azure" }) {
  const [platform, setPlatform] = useState(initialPlatform);
  const [name, setName] = useState("");
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;
  const meta = providers.find((p) => p.key === platform);

  useEffect(() => {
    if (!open) return;
    setPlatform(editing ? editing.platform : initialPlatform);
    setName(editing ? editing.name : "");
    setCreds(editing ? { ...editing.summary } : {});
  }, [open, editing, initialPlatform]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = isEdit
        ? await client.patch(`/platforms/${editing.id}`, { name, credentials: creds })
        : await client.post("/platforms", { name, platform, credentials: creds });
      toast.success(data.last_test?.ok ? `Connected · ${data.last_test.login}` : "Saved (credential test failed)");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!meta) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="platform-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? `Edit platform · ${editing.name}` : "New platform connector"}</DialogTitle>
          <DialogDescription>{isEdit ? "Leave secret fields empty to keep the current values. Saving re-runs the credential test." : "Credentials are encrypted at rest and only used for read-only queries by Patrice AI."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="platform-form">
          <PlatformPicker value={platform} disabled={isEdit} hint={meta.hint} onChange={(k) => { setPlatform(k); setCreds({}); }} />
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label>
            <Input required data-testid="platform-name" className="mt-2 h-10 border-slate-200" placeholder={`Production ${meta.label}`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {meta.fields.map((f) => (
            <CredentialField key={f.key} field={f} isEdit={isEdit} value={creds[f.key] || ""} onChange={(v) => setCreds((c) => ({ ...c, [f.key]: v }))} />
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="h-10 bg-slate-900 text-white hover:bg-slate-800" data-testid="platform-submit">
              <KeyRound className="mr-2 h-4 w-4" /> {submitLabelFor(saving, isEdit)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-0.5 truncate font-mono-tight text-slate-800" title={value}>{value || "—"}</p>
    </div>
  );
}

function PlatformActions({ p, isAdmin, busy, onTest, onEdit, onRemove }) {
  if (!isAdmin) {
    return <span className="font-mono-tight text-[11px] text-slate-400">last test: {p.last_test?.at ? new Date(p.last_test.at).toLocaleDateString() : "—"}</span>;
  }
  return (
    <>
      <Button variant="ghost" size="sm" onClick={onTest} disabled={busy} className="h-8 text-slate-600" data-testid={`platform-test-${p.name}`}>
        <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", busy && "animate-spin")} /> Test credentials
      </Button>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit} className="h-8 text-slate-600" data-testid={`platform-edit-${p.name}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
        <Button variant="ghost" size="icon" onClick={onRemove} className="h-8 w-8" data-testid={`platform-delete-${p.name}`}><Trash2 className="h-4 w-4 text-slate-500" /></Button>
      </div>
    </>
  );
}

export function PlatformCard({ p, isAdmin, onChanged, onDeleted, onEdit }) {
  const [busy, setBusy] = useState(false);
  const ok = p.last_test?.ok;
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/platforms/${p.id}/test`);
      onChanged({ ...p, last_test: data });
      data.ok ? toast.success(`Connected · ${data.login}`) : toast.error(data.error);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove platform connector "${p.name}"?`)) return;
    await client.delete(`/platforms/${p.id}`);
    onDeleted(p.id);
    toast.success("Platform connector removed");
  };
  const facts = Object.entries(p.summary || {}).filter(([, v]) => v);
  return (
    <Card className="border border-slate-200 bg-white p-5 shadow-none" data-testid={`platform-${p.name}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center border border-slate-200 bg-slate-50"><CloudIcon cloud={p.platform} className="h-5 w-5" /></div>
          <div>
            <div className="font-display text-base font-semibold text-slate-900">{p.name}</div>
            <div className="font-mono-tight text-[11px] text-slate-500">{p.platform_label} · read-only</div>
          </div>
        </div>
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest", ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")} data-testid={`platform-status-${p.name}`}>
          {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          {statusLabel(p)}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        {facts.map(([k, v]) => <Fact key={k} label={k.replace(/_/g, " ")} value={v} />)}
        <Fact label="identity" value={p.last_test?.login} />
      </div>
      {p.last_test && !ok && (
        <p className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 font-mono-tight text-[11px] text-amber-800" data-testid={`platform-error-${p.name}`}>{p.last_test.error}</p>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <PlatformActions p={p} isAdmin={isAdmin} busy={busy} onTest={test} onEdit={() => onEdit(p)} onRemove={remove} />
      </div>
    </Card>
  );
}

export function PlatformsEmpty({ isAdmin }) {
  return (
    <div className="border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="platforms-empty">
      <Cloud className="mx-auto h-8 w-8 text-slate-300" />
      <h2 className="mt-4 font-display text-lg font-semibold">No platform connector yet</h2>
      <p className="mt-1 text-sm text-slate-500">{isAdmin ? "Add an Azure service principal, AWS access keys or a GCP service account so Patrice AI can query your estate." : "Ask a workspace admin to add a platform connector."}</p>
    </div>
  );
}
